import type { ApolloError } from '@apollo/client'
import {
  GroupParticipationFragmentFragment,
  useConfirmGroupParticipationMutation,
  useDestroyGroupParticipationMutation
} from '@lopo-tech/lopo-api'
import React, { FunctionComponent, useCallback, useMemo } from 'react'
import { IUserListAction, IUserListAdminAction } from 'src/components/item_segment/layouts/profiles_list/model'
import ProfilesListItemSegment from 'src/components/item_segment/layouts/profiles_list/profiles_list'
import { showSnackBar } from 'src/components/snack_bar/snack_bar'
import { textResources } from 'src/lang/de'
import { UrlUtils } from 'src/utils/url/url'

const labels = textResources.groups

type IProps = {
  participation: GroupParticipationFragmentFragment;
};

const ProfileItem: FunctionComponent<IProps> = ({ participation }) => {
  const id = participation.participant?.id ?? ''
  const name = participation.participant?.name ?? ''
  const description = participation.participant?.description ?? ''
  const avatar = participation.participant?.avatar
  const sponsorLevel = participation.participant?.shapeSponsorshipLevel ?? undefined
  const type = participation?.participant.__typename

  const [confirmGroupParticipation] = useConfirmGroupParticipationMutation()
  const [destroyGroupParticipation] = useDestroyGroupParticipationMutation()

  const onConfirm = useCallback(
    async (participation: GroupParticipationFragmentFragment): Promise<void> => {
      try {
        await confirmGroupParticipation({
          variables: { input: { id: participation.id } }
        })
        showSnackBar(labels.confirmMemberSuccess(participation.participant.name))
      } catch (err) {
        showSnackBar(labels.confirmMemberFail(participation.participant.name))
        console.error('confirmMember error', err as ApolloError)
      }
    },
    [confirmGroupParticipation]
  )

  const onRemove = useCallback(
    async (participation: GroupParticipationFragmentFragment): Promise<void> => {
      try {
        await destroyGroupParticipation({
          variables: { input: { id: participation.id } }
        })
        showSnackBar(labels.eliminateMemberSuccess(participation.participant.name))
      } catch (err) {
        showSnackBar(labels.eliminateMemberFail(participation.participant.name))
        console.error('eliminateMember error', err as ApolloError)
      }
    },
    [destroyGroupParticipation]
  )

  const action = useMemo(() => {
    const a: IUserListAction & IUserListAdminAction = {}

    if (participation.canConfirm?.value && onConfirm) {
      a.accept = { action: () => onConfirm(participation) }
    }

    if (participation.canDestroy?.value && onRemove) {
      a.remove = { action: () => onRemove(participation) }
    }

    if (participation.participant?.canFollow?.value) {
      a.follow = { actorProfile: participation.participant }
    }

    return Object.keys(a).length ? a : undefined
  }, [participation])

  return (
    <ProfilesListItemSegment
      key={id}
      avatar={avatar}
      shapeSponsorshipLevel={sponsorLevel}
      label={name}
      description={description}
      action={action}
      navigation={{ url: UrlUtils.getProfileFrontendPath(participation.participant.slug, type), featureBlocked: !participation.participant.canShow.value }}
    />
  )
}

export default ProfileItem
